// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { CaretDown } from "@phosphor-icons/react";
import { Checkbox, Popover, Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  REVIEW_COMMENT_TAG_IDS,
  type ReviewCommentTagId,
  reviewCommentTagLabelKey,
} from "./reviewCommentTags";

type AskV2ReviewTagFilterProps = {
  activeTags: ReadonlySet<ReviewCommentTagId>;
  onToggle: (tag: ReviewCommentTagId, checked: boolean) => void;
};

function filterTriggerLabel(
  activeTags: ReadonlySet<ReviewCommentTagId>,
  labelFor: (tag: ReviewCommentTagId) => string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (activeTags.size === REVIEW_COMMENT_TAG_IDS.length) {
    return t("askV2.review.tagFilterAll");
  }
  if (activeTags.size === 0) {
    return t("askV2.review.tagFilterNone");
  }
  if (activeTags.size === 1) {
    const only = Array.from(activeTags)[0];
    return only
      ? labelFor(only)
      : t("askV2.review.tagFilterCount", { count: 1 });
  }
  return t("askV2.review.tagFilterCount", { count: activeTags.size });
}

export function AskV2ReviewTagFilter({
  activeTags,
  onToggle,
}: AskV2ReviewTagFilterProps) {
  const { t } = useTranslation("ui");

  const labelFor = useMemo(
    () => (tag: ReviewCommentTagId) => t(reviewCommentTagLabelKey(tag)),
    [t],
  );

  const triggerLabel = filterTriggerLabel(activeTags, labelFor, t);

  return (
    <Popover.Root>
      <Popover.Trigger>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-2"
          data-testid="ask-v2-review-tag-filter"
          aria-label={t("askV2.review.tagFilterLabel")}
        >
          <span>{triggerLabel}</span>
          <CaretDown size={12} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Content
        className="max-h-[min(70vh,420px)] overflow-y-auto p-2"
        data-testid="ask-v2-review-tag-filter-menu"
        aria-label={t("askV2.review.tagFilterLabel")}
      >
        <ul className="space-y-1">
          {REVIEW_COMMENT_TAG_IDS.map((tag) => {
            const checked = activeTags.has(tag);
            const label = labelFor(tag);
            return (
              <li key={tag}>
                <label
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-puntt-neutral-gray-2"
                  data-testid={`ask-v2-review-tag-filter-${tag}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => onToggle(tag, value === true)}
                  />
                  <Text size="2">{label}</Text>
                </label>
              </li>
            );
          })}
        </ul>
      </Popover.Content>
    </Popover.Root>
  );
}
